import { ADMOB_IS_TEST, ADMOB_REWARDED, adsAreReal, platform } from './config';

export type AdOutcome = 'rewarded' | 'closed' | 'unavailable';

export interface AdProvider {
  readonly mode: 'test' | 'admob';
  /** consent (EU/UK), App Tracking Transparency (iOS), SDK start */
  init(): Promise<void>;
  /** the reward may only be given when this returns 'rewarded' (§10) */
  showRewarded(): Promise<AdOutcome>;
}

/**
 * TEST MODE: a clearly marked 5-second fake video. Closing early gives no
 * reward – exactly like a real rewarded ad.
 */
export class TestAdProvider implements AdProvider {
  readonly mode = 'test' as const;
  /** set by the UI: plays the fake video, resolves true when watched to the end */
  play: () => Promise<boolean> = async () => true;

  async init(): Promise<void> {}

  async showRewarded(): Promise<AdOutcome> {
    return (await this.play()) ? 'rewarded' : 'closed';
  }
}

/**
 * Google AdMob rewarded videos. Order at start-up, as Google requires:
 *   1. UMP consent form where needed (EU / UK / Switzerland),
 *   2. iOS App Tracking Transparency prompt,
 *   3. start the SDK. Ads are only requested when consent allows it.
 */
export class AdMobProvider implements AdProvider {
  readonly mode = 'admob' as const;
  private admob?: typeof import('@capacitor-community/admob').AdMob;
  private canRequest = false;

  async init(): Promise<void> {
    const { AdMob, AdmobConsentStatus } = await import('@capacitor-community/admob');
    this.admob = AdMob;
    try {
      let consent = await AdMob.requestConsentInfo();
      if (consent.isConsentFormAvailable && consent.status === AdmobConsentStatus.REQUIRED) {
        consent = await AdMob.showConsentForm();
      }
      this.canRequest = consent.canRequestAds;
      if (platform === 'ios') {
        const { status } = await AdMob.trackingAuthorizationStatus();
        if (status === 'notDetermined') await AdMob.requestTrackingAuthorization();
      }
      await AdMob.initialize({ initializeForTesting: ADMOB_IS_TEST });
    } catch (e) {
      console.warn('AdMob start failed – rewarded videos disabled', e);
      this.canRequest = false;
    }
  }

  async showRewarded(): Promise<AdOutcome> {
    if (!this.admob || !this.canRequest || !ADMOB_REWARDED) return 'unavailable';
    const { RewardAdPluginEvents } = await import('@capacitor-community/admob');
    let rewarded = false;
    const handle = await this.admob.addListener(RewardAdPluginEvents.Rewarded, () => (rewarded = true));
    try {
      await this.admob.prepareRewardVideoAd({ adId: ADMOB_REWARDED, isTesting: ADMOB_IS_TEST });
      await this.admob.showRewardVideoAd();
      return rewarded ? 'rewarded' : 'closed';
    } catch {
      return rewarded ? 'rewarded' : 'unavailable';
    } finally {
      await handle.remove();
    }
  }
}

export function createAdProvider(): AdProvider {
  return adsAreReal ? new AdMobProvider() : new TestAdProvider();
}
